import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../utils/api';
import { errorText } from '../../utils/errorText';
import { box, button, input, label, mono } from '../shared/styles';

/*
 * Ask the lab server for a DRAFT interpretation of this analysis. First the
 * exact results that would leave -- numbers and design tokens, no measured
 * values, codes or names -- then the request, carrying the confirmation of
 * what was shown. Desktop only: the cloud answers 501 for lab links.
 */
export default function SynthesisPanel({ base, onReport }) {
  const { t } = useTranslation('experiments');
  const [links, setLinks] = useState(null);
  const [linkId, setLinkId] = useState('');
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get('/research/links')
      .then(({ data }) => {
        if (!alive) return;
        const offering = (data.links || []).filter((l) => (l.capabilities || []).includes('workflow:experiment_synthesis'));
        setLinks(offering);
        if (offering[0]) setLinkId(offering[0].link_id);
      })
      .catch((e) => { if (alive) setLinks(e?.response?.status === 501 ? 'desktop' : []); });
    return () => { alive = false; };
  }, []);

  const show = async () => {
    setError('');
    try {
      setPreview((await api.get(`${base}/pack`)).data);
    } catch (e) {
      setError(errorText(e));
    }
  };

  const send = async () => {
    setBusy(true);
    setError('');
    try {
      const { data } = await api.post(`${base}/synthesis`, { link_id: linkId, confirmation: preview.confirmation });
      setPreview(null);
      onReport(data.report.report_id);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section style={box} data-testid="synthesis-panel">
      <h4 style={{ marginTop: 0 }}>{t('synthesisTitle')}</h4>
      <p style={{ fontSize: 13, color: 'var(--c-text-muted-fg)' }}>{t('synthesisIntro')}</p>
      {links === 'desktop' && <p>{t('desktopOnly')}</p>}
      {Array.isArray(links) && links.length === 0 && <p>{t('noServer')}</p>}
      {Array.isArray(links) && links.length > 0 && (
        <>
          <label style={label}>{t('labServer')}
            <select style={input} value={linkId} title={t('tip.labServer')} onChange={(e) => setLinkId(e.target.value)}>
              {links.map((l) => <option key={l.link_id} value={l.link_id}>{l.label}</option>)}
            </select>
          </label>
          {!preview && <button type="button" onClick={show} title={t('tip.previewPack')} data-testid="preview-pack">{t('previewPack')}</button>}
          {preview && (
            <>
              <p style={{ fontSize: 13 }}>{preview.notice}</p>
              <pre style={{ ...mono, maxHeight: 240, overflow: 'auto', background: 'var(--c-bg-subtle)', padding: 8 }}
                data-testid="pack-json">{JSON.stringify(preview.pack.experiment_results, null, 2)}</pre>
              <button type="button" style={button('var(--c-primary)')} onClick={send} disabled={busy}
                title={t('tip.sendSynthesis')} data-testid="send-synthesis">{t('sendSynthesis')}</button>
            </>
          )}
        </>
      )}
      {error && <p role="alert" style={{ color: 'var(--c-danger-fg)' }}>{error}</p>}
    </section>
  );
}
